import { useTranslation } from 'react-i18next';
import {
  ActionIcon,
  Card,
  Fieldset,
  Grid,
  Group,
  NumberInput,
  Select,
  Text,
  TextInput,
  Title,
  Tooltip
} from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import LoaderOverlay from '../../components/LoaderOverlay/LoaderOverlay';
import { useEffect } from 'react';
import { useGetCashUpReportPreviewData } from '../../hooks/cashups/queries';
import { useGetCashierboxes } from '../../hooks/cashierboxes/queries';
import { formatMoneyString } from '../../utils/strings';
import { useSelector } from 'react-redux';
import useCanUseMercadoPago from '../../hooks/useCanUseMercadoPago';
import { getInitialCashAmount } from '../../utils/initialCash';
import CashupCalculationDetail from './CashupCalculationDetail';

const HelpIcon = ({ tooltip }: { tooltip: string }) => (
  <Tooltip label={tooltip} multiline width={260} withArrow position="top">
    <ActionIcon
      variant="subtle"
      size="sm"
      aria-label={tooltip}
      color="gray"
    >
      <IconInfoCircle size={16} />
    </ActionIcon>
  </Tooltip>
);

const HelpTooltipLabel = ({ label, tooltip }: { label: string; tooltip: string }) => (
  <Group gap={4} align="center" wrap="nowrap">
    <Text span fw="bold">
      {label}
    </Text>
    <HelpIcon tooltip={tooltip} />
  </Group>
);

const CreateEditForm = ({
  form,
  storeId,
  isCashOnly,
  showNonCashSummary
}: {
  form: any;
  storeId: string;
  isCashOnly: boolean;
  showNonCashSummary: boolean;
}) => {
  const { t } = useTranslation(['CashupsPage', 'FieldName']);
  const user = useSelector((state) => state.user);
  const currentUserId = user?.user?._id || user?._id || '';
  const showCashupTotals = user?.permission['hideCashupTotals']?.canView;
  const { isLoading: isGetCashierboxesLoading, data: cashierboxes } = useGetCashierboxes(
    !!storeId,
    storeId
  );
  const selectedCashierBox = form.values.cashierBox || '';
  const { storeCanUseMP } = useCanUseMercadoPago(storeId);
  const totalsColorsRules = (value: number) => {
    if (value === 0) return 'blue';
    else if (value < 0) return 'red';
    else if (value > 0) return 'green';
  };

  const { isLoading: isGetPreviewDataLoading, data: cashupPreviewData } =
    useGetCashUpReportPreviewData(storeId, selectedCashierBox);
  const savedInitialCashAmount =
    storeId && currentUserId
      ? getInitialCashAmount({
          storeId,
          userId: currentUserId,
        })
      : null;

  useEffect(() => {
    if (!selectedCashierBox) return;
    if (!cashupPreviewData) return;

    const lastCashupReportLeftInDrawer = cashupPreviewData.lastCashupReportLeftInDrawer || 0;
    const nextStartingCash =
      typeof savedInitialCashAmount === 'number'
        ? savedInitialCashAmount
        : lastCashupReportLeftInDrawer;

    if (form.values.startingCash === nextStartingCash) return;
    form.setFieldValue('startingCash', nextStartingCash);
  }, [cashupPreviewData, savedInitialCashAmount, selectedCashierBox]);

  useEffect(() => {
    if (!isCashOnly) return;

    if (form.values.declaredCard !== 0) {
      form.setFieldValue('declaredCard', 0);
    }
    if (form.values.declaredTransfer !== 0) {
      form.setFieldValue('declaredTransfer', 0);
    }
    if (form.values.declaredMercadoPago !== 0) {
      form.setFieldValue('declaredMercadoPago', 0);
    }
  }, [
    form,
    form.values.declaredCard,
    form.values.declaredMercadoPago,
    form.values.declaredTransfer,
    isCashOnly
  ]);

  useEffect(() => {
    if (storeCanUseMP) return;
    if (form.values.declaredMercadoPago !== 0) {
      form.setFieldValue('declaredMercadoPago', 0);
    }
  }, [form, form.values.declaredMercadoPago, storeCanUseMP]);

  const calculatedCash = cashupPreviewData?.calculatedCash || 0;
  const calculatedCard = cashupPreviewData?.calculatedCard || 0;
  const calculatedTransfer = cashupPreviewData?.calculatedTransfer || 0;
  const calculatedMercadoPago = cashupPreviewData?.calculatedMercadoPago || 0;

  const declaredCash = Number(form.values.declaredCash) || 0;
  const declaredCard = Number(form.values.declaredCard) || 0;
  const declaredTransfer = Number(form.values.declaredTransfer) || 0;
  const declaredMercadoPago = Number(form.values.declaredMercadoPago) || 0;

  const cashDiff = declaredCash - calculatedCash;
  const cardDiff = declaredCard - calculatedCard;
  const transferDiff = declaredTransfer - calculatedTransfer;
  const mercadoPagoDiff = declaredMercadoPago - calculatedMercadoPago;
  const declaredTotal = isCashOnly
    ? declaredCash
    : declaredCash + declaredCard + declaredTransfer + declaredMercadoPago;
  const calculatedTotal = isCashOnly
    ? calculatedCash
    : calculatedCash + calculatedCard + calculatedTransfer + calculatedMercadoPago;
  const totalDiff = declaredTotal - calculatedTotal;
  const shouldShowNonCashSummary = isCashOnly && showNonCashSummary && showCashupTotals;

  if (isGetCashierboxesLoading || isGetPreviewDataLoading) return <LoaderOverlay visible />;

  return (
    <Fieldset>
      <Grid gutter="md">
        <Grid.Col span={{ xs: 12, md: 6 }}>
          <Card>
            <Card.Section inheritPadding withBorder py="md">
              <Title order={4}> {t('CashupsPage:CRUD.info')}</Title>
            </Card.Section>
            <Card.Section inheritPadding py="md">
              <Grid>
                <Grid.Col span={12}>
                  <Select
                    allowDeselect={false}
                    label={t('FieldName:cashierBox')}
                    description={t('CashupsPage:CRUD.tooltips.cashierBox')}
                    placeholder={t('FieldName:cashierBox')}
                    data={(cashierboxes || []).map((cashierbox) => {
                      return {
                        label: cashierbox.name,
                        value: cashierbox._id,
                        key: cashierbox._id
                      };
                    })}
                    {...form.getInputProps('cashierBox')}
                  />
                </Grid.Col>
                <Grid.Col span={12}>
                  <NumberInput
                    {...form.getInputProps('startingCash')}
                    allowDecimal
                    decimalScale={2}
                    label={t('FieldName:startingCash')}
                    description={t('CashupsPage:CRUD.tooltips.startingCash')}
                    placeholder={t('FieldName:startingCash')}
                  />
                </Grid.Col>
                <Grid.Col span={12}>
                  <NumberInput
                    {...form.getInputProps('cashToBank')}
                    allowDecimal
                    decimalScale={2}
                    label={t('FieldName:cashToBank')}
                    description={t('CashupsPage:CRUD.tooltips.cashToBank')}
                    placeholder={t('FieldName:cashToBank')}
                  />
                </Grid.Col>
                <Grid.Col span={12}>
                  <TextInput
                    label={t('FieldName:notes')}
                    description={t('CashupsPage:CRUD.tooltips.notes')}
                    placeholder={t('FieldName:notes')}
                    {...form.getInputProps('notes')}
                  />
                </Grid.Col>
              </Grid>
            </Card.Section>
          </Card>
        </Grid.Col>
        <Grid.Col span={{ xs: 12, md: 6 }} p={0}>
          <Grid.Col span={12}>
            <Card>
              <Card.Section inheritPadding withBorder py="md">
                <Title order={4}> {t('CashupsPage:CRUD.salesDeclaration')}</Title>
                <Text size="sm" c="dimmed">
                  {t('CashupsPage:CRUD.tooltips.salesDeclaration')}
                </Text>
              </Card.Section>
              <Card.Section inheritPadding py="md">
                <Grid>
                  <Grid.Col span={12}>
                    <Grid>
                      <Grid.Col span={3}>
                        <HelpTooltipLabel
                          label={t('CashupsPage:cashUpReport.concept')}
                          tooltip={t('CashupsPage:CRUD.tooltips.concept')}
                        />
                      </Grid.Col>
                      <Grid.Col span={3}>
                        <HelpTooltipLabel
                          label={t('CashupsPage:cashUpReport.counted')}
                          tooltip={t('CashupsPage:CRUD.tooltips.counted')}
                        />
                      </Grid.Col>
                      {showCashupTotals && (
                        <>
                          <Grid.Col span={3}>
                            <HelpTooltipLabel
                              label={t('CashupsPage:cashUpReport.calculated')}
                              tooltip={t('CashupsPage:CRUD.tooltips.calculated')}
                            />
                          </Grid.Col>
                          <Grid.Col span={3}>
                            <HelpTooltipLabel
                              label={t('CashupsPage:cashUpReport.difference')}
                              tooltip={t('CashupsPage:CRUD.tooltips.difference')}
                            />
                          </Grid.Col>
                        </>
                      )}
                    </Grid>
                  </Grid.Col>
                  <Grid.Col span={12}>
                    <Grid>
                      <Grid.Col span={3}>
                        <HelpTooltipLabel
                          label={t('CashupsPage:cashUpReport.cash')}
                          tooltip={t('CashupsPage:CRUD.tooltips.cash')}
                        />
                      </Grid.Col>
                      <Grid.Col span={3}>
                        <NumberInput
                          {...form.getInputProps('declaredCash')}
                          allowDecimal
                          decimalScale={2}
                          placeholder={t('FieldName:declaredCash')}
                        />
                      </Grid.Col>
                      {showCashupTotals && (
                        <>
                          <Grid.Col span={3}>
                            <Text>{formatMoneyString(cashupPreviewData?.calculatedCash || 0)}</Text>
                          </Grid.Col>
                          <Grid.Col span={3}>
                            <Text c={totalsColorsRules(cashDiff)}>
                              {formatMoneyString(cashDiff || 0)}
                            </Text>
                          </Grid.Col>
                        </>
                      )}
                    </Grid>
                  </Grid.Col>
                  {!isCashOnly && (
                    <Grid.Col span={12}>
                      <Grid>
                        <Grid.Col span={3}>
                          <HelpTooltipLabel
                            label={t('CashupsPage:cashUpReport.card')}
                            tooltip={t('CashupsPage:CRUD.tooltips.card')}
                          />
                        </Grid.Col>
                        <Grid.Col span={3}>
                          <NumberInput
                            {...form.getInputProps('declaredCard')}
                            allowDecimal
                            decimalScale={2}
                            placeholder={t('FieldName:declaredCard')}
                          />
                        </Grid.Col>
                        {showCashupTotals && (
                          <>
                            <Grid.Col span={3}>
                              <Text>{formatMoneyString(calculatedCard)}</Text>
                            </Grid.Col>
                            <Grid.Col span={3}>
                              <Text c={totalsColorsRules(cardDiff)}>
                                {formatMoneyString(cardDiff || 0)}
                              </Text>
                            </Grid.Col>
                          </>
                        )}
                      </Grid>
                    </Grid.Col>
                  )}
                  {!isCashOnly && (
                    <Grid.Col span={12}>
                      <Grid>
                        <Grid.Col span={3}>
                          <HelpTooltipLabel
                            label={t('CashupsPage:cashUpReport.transfer')}
                            tooltip={t('CashupsPage:CRUD.tooltips.transfer')}
                          />
                        </Grid.Col>
                        <Grid.Col span={3}>
                          <NumberInput
                            {...form.getInputProps('declaredTransfer')}
                            allowDecimal
                            decimalScale={2}
                            placeholder={t('FieldName:declaredTransfer')}
                          />
                        </Grid.Col>
                        {showCashupTotals && (
                          <>
                            <Grid.Col span={3}>
                              <Text>{formatMoneyString(calculatedTransfer)}</Text>
                            </Grid.Col>
                            <Grid.Col span={3}>
                              <Text c={totalsColorsRules(transferDiff)}>
                                {formatMoneyString(transferDiff || 0)}
                              </Text>
                            </Grid.Col>
                          </>
                        )}
                      </Grid>
                    </Grid.Col>
                  )}
                  {!isCashOnly && storeCanUseMP && (
                    <Grid.Col span={12}>
                      <Grid>
                        <Grid.Col span={3}>
                          <HelpTooltipLabel
                            label={t('CashupsPage:cashUpReport.mercadopago')}
                            tooltip={t('CashupsPage:CRUD.tooltips.mercadoPago')}
                          />
                        </Grid.Col>
                        <Grid.Col span={3}>
                          <NumberInput
                            {...form.getInputProps('declaredMercadoPago')}
                            allowDecimal
                            decimalScale={2}
                            placeholder={t('FieldName:declaredMercadoPago')}
                          />
                        </Grid.Col>
                        {showCashupTotals && (
                          <>
                            <Grid.Col span={3}>
                              <Text>{formatMoneyString(calculatedMercadoPago)}</Text>
                            </Grid.Col>
                            <Grid.Col span={3}>
                              <Text c={totalsColorsRules(mercadoPagoDiff)}>
                                {formatMoneyString(mercadoPagoDiff || 0)}
                              </Text>
                            </Grid.Col>
                          </>
                        )}
                      </Grid>
                    </Grid.Col>
                  )}
                  <Grid.Col span={12}>
                    <Grid>
                      <Grid.Col span={3}>
                        <HelpTooltipLabel
                          label={t('CashupsPage:cashUpReport.total')}
                          tooltip={t('CashupsPage:CRUD.tooltips.total')}
                        />
                      </Grid.Col>
                      <Grid.Col span={3}>
                        <Text>{formatMoneyString(declaredTotal)}</Text>
                      </Grid.Col>
                      {showCashupTotals && (
                        <>
                          <Grid.Col span={3}>
                            <Text>{formatMoneyString(calculatedTotal)}</Text>
                          </Grid.Col>
                          <Grid.Col span={3}>
                            <Text c={totalsColorsRules(totalDiff)}>{formatMoneyString(totalDiff)}</Text>
                          </Grid.Col>
                        </>
                      )}
                    </Grid>
                  </Grid.Col>
                  {shouldShowNonCashSummary && (
                    <>
                      <Grid.Col span={12}>
                        <Group gap={4} align="center" wrap="nowrap">
                          <Title order={5}>{t('CashupsPage:CRUD.nonCashSummaryTitle')}</Title>
                          <HelpIcon tooltip={t('CashupsPage:CRUD.tooltips.nonCashSummary')} />
                        </Group>
                      </Grid.Col>
                      <Grid.Col span={12}>
                        <Grid>
                          <Grid.Col span={6} fw="bold">
                            {t('CashupsPage:cashUpReport.card')}
                          </Grid.Col>
                          <Grid.Col span={6}>
                            <Text>{formatMoneyString(calculatedCard)}</Text>
                          </Grid.Col>
                          <Grid.Col span={6} fw="bold">
                            {t('CashupsPage:cashUpReport.transfer')}
                          </Grid.Col>
                          <Grid.Col span={6}>
                            <Text>{formatMoneyString(calculatedTransfer)}</Text>
                          </Grid.Col>
                          {storeCanUseMP && (
                            <>
                              <Grid.Col span={6} fw="bold">
                                {t('CashupsPage:cashUpReport.mercadopago')}
                              </Grid.Col>
                              <Grid.Col span={6}>
                                <Text>{formatMoneyString(calculatedMercadoPago)}</Text>
                              </Grid.Col>
                            </>
                          )}
                        </Grid>
                      </Grid.Col>
                    </>
                  )}
                  {showCashupTotals && (
                    <Grid.Col span={12}>
                      <CashupCalculationDetail
                        preview={cashupPreviewData}
                        isCashOnly={isCashOnly}
                      />
                    </Grid.Col>
                  )}
                </Grid>
              </Card.Section>
            </Card>
          </Grid.Col>
        </Grid.Col>
      </Grid>
    </Fieldset>
  );
};

export default CreateEditForm;
